import { LucideIcon } from 'lucide-react';
import { photoUrl, useSite } from '../../utils/site';

/** The photo uploaded for `slot` (Administration -> Appearance -> Photos), else the placeholder. */
export function SlotPhoto({ slot, icon, label, aspect = 'aspect-[4/3]' }: { slot: string; icon: LucideIcon; label: string; aspect?: string }) {
  const url = photoUrl(useSite(), slot);
  if (!url) return <PlaceholderTile icon={icon} label={label} aspect={aspect} />;
  return (
    <div className={`${aspect} w-full overflow-hidden rounded-xl shadow-card`}>
      <img src={url} alt={label.replace(/^Add photo:\s*/i, '')} className="h-full w-full object-cover" loading="lazy" />
    </div>
  );
}

/**
 * A stand-in for a photo that isn't ready yet - shown to visitors as
 * "Photo coming soon" with a short caption of what it will show, never as
 * a fake stock photo. Labels are written "Add photo: <what>" in the pages
 * so it's clear what belongs in each slot; the prefix is dropped here.
 */
export default function PlaceholderTile({
  icon: Icon,
  label,
  aspect = 'aspect-[4/3]',
}: {
  icon: LucideIcon;
  label: string;
  aspect?: string;
}) {
  const caption = label.replace(/^Add photo:\s*/i, '');
  return (
    <div
      className={`group relative flex ${aspect} w-full flex-col items-center justify-center gap-3 overflow-hidden rounded-xl border border-brand-green-100 bg-gradient-to-br from-brand-green-50 via-surface-subtle to-brand-gold-50 p-4 text-center`}
    >
      <span className="skg-float flex h-14 w-14 items-center justify-center rounded-full bg-surface shadow-card">
        <Icon className="h-7 w-7 text-brand-green-600" strokeWidth={1.6} />
      </span>
      <span className="rounded-full bg-brand-gold-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-brand-gold-700">
        Photo coming soon
      </span>
      <p className="text-sm font-medium text-ink-500">{caption}</p>
    </div>
  );
}
